/**
 * ── Calendar holds ───────────────────────────────────────────────────────────
 *
 * Holds belong to the ROLE's slots and each one names the candidate it was
 * offered to. Every response returns the role's full hold list, so the overview
 * in the panel section is refreshed by the same call that changed it rather
 * than by a follow-up read that could disagree.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { tracked } from './taActionBoardAnalytics';
import type { GradedSlot, SlotHold, TrackingEntry } from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

export interface HoldsResponse {
  ok: boolean;
  holds: SlotHold[];
}

export async function fetchHolds(
  postingIds: string[]
): Promise<{ holds: Record<string, SlotHold[]>; legacy: Record<string, number> }> {
  const ids = postingIds.filter(Boolean).slice(0, 50);
  if (ids.length === 0) return { holds: {}, legacy: {} };
  const res = await authenticatedFetch(`${BASE}/holds?postingIds=${encodeURIComponent(ids.join(','))}`);
  return res.json() as Promise<{ holds: Record<string, SlotHold[]>; legacy: Record<string, number> }>;
}

/** Block the given slots. Slots already held are skipped, never blocked twice. */
export async function postHolds(
  postingId: string,
  slots: GradedSlot[]
): Promise<
  HoldsResponse & {
    created: number;
    skipped: { alreadyHeld: number; overCapacity: number };
    failed: { start: string; reason?: string }[];
    /** Set only when NO hold was created and every failure shares one cause. */
    error?: string;
  }
> {
  return tracked(
    'ta_board_holds',
    async () => {
      const res = await authenticatedFetch(`${BASE}/holds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postingId, slots }),
      });
      return res.json() as Promise<
        HoldsResponse & {
          created: number;
          skipped: { alreadyHeld: number; overCapacity: number };
          /** `reason` is the calendar's own status+message, when the proxy could name one. */
          failed: { start: string; reason?: string }[];
          error?: string;
        }
      >;
    },
    // candidate_count is gone: a hold belongs to a slot now, not to the set of
    // waiting candidates it used to be copied across.
    { slot_count: slots.length }
  );
}

/**
 * Delete the calendar holds on the named slots — permanent, and the recruiter
 * has already confirmed it by unticking the slot in the overview.
 *
 * `{ postingId, legacy: true }` instead drains holds left over from the previous
 * shape, which the per-slot overview cannot address.
 */
export async function releaseHoldSlots(
  target: { postingId: string; starts: string[] } | { postingId: string; legacy: true }
): Promise<HoldsResponse & { released: number; failed: number }> {
  return tracked(
    'ta_board_holds_released',
    async () => {
      const res = await authenticatedFetch(`${BASE}/holds/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(target),
      });
      return res.json() as Promise<HoldsResponse & { released: number; failed: number }>;
    },
    // Counts and scope only — never a slot time, which is candidate-adjacent.
    'legacy' in target ? { scope: 'legacy' } : { scope: 'slot', slot_count: target.starts.length }
  );
}

/** Record which held slots went to which candidate. */
export async function recordOfferedSlots(payload: {
  postingId: string;
  opportunityId: string;
  candidateName: string;
  starts: string[];
}): Promise<HoldsResponse> {
  const res = await authenticatedFetch(`${BASE}/holds/offer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<HoldsResponse>;
}

/**
 * The candidate declined / the offer was withdrawn. Their slots read free again
 * in the overview and the calendar holds STAY until released there.
 */
export async function declineOffer(payload: {
  postingId: string;
  opportunityId: string;
}): Promise<HoldsResponse & { declined: number; entry: TrackingEntry; status: string }> {
  return tracked(
    'ta_board_withdraw',
    async () => {
      const res = await authenticatedFetch(`${BASE}/holds/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return res.json() as Promise<
        HoldsResponse & { declined: number; entry: TrackingEntry; status: string }
      >;
    },
    { scope: 'candidate' }
  );
}

export interface CaseBookingResult {
  ok: boolean;
  booking: { start: string; end: string; meetLink: string | null; htmlLink: string | null };
  leverCreated: boolean;
  /**
   * Did the interviewers' invite get the Lever feedback-form link?
   *
   * Separate from `leverCreated`: the interview can exist in Lever while the calendar patch that
   * puts the form on the event failed — and the invite is where the panel reaches the scorecard
   * from, so they would open it and find nothing to click. Absent on an older proxy.
   */
  feedbackLinkAdded?: boolean;
  emailSent: boolean;
  holdsReleased: number;
  /** The panel is longer than any link length — self-service reschedule cannot offer it. */
  rescheduleCapped: boolean;
  managePageUrl: string;
  /** true = a free room was reserved; false = asked but none free / it declined; null = no room requested. */
  roomBooked?: boolean | null;
  /** Name of the room that was reserved, when one was. */
  roomName?: string | null;
}

/**
 * Book a confirmed case: calendar event with the panel, Lever interview with
 * the feedback form, and the candidate's confirmation email + .ics.
 *
 * The candidate's name and address are resolved from Lever server-side and are
 * deliberately NOT sent from here.
 */
export async function bookCaseInterview(payload: {
  postingId: string;
  opportunityId: string;
  roleTitle: string;
  start: string;
  end?: string;
  /** 'onsite' (room + office) or 'remote' (Google Meet); defaults remote server-side. */
  mode?: 'onsite' | 'remote';
  /** Onsite only: when true, the server books the first shortlisted room free for the slot. */
  bookRoom?: boolean;
  /** Onsite only: number the candidate calls on arrival. */
  contactPhone?: string;
}): Promise<CaseBookingResult> {
  const res = await authenticatedFetch(`${BASE}/case-booking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<CaseBookingResult>;
}
